import { Fragment, useState, type ReactNode } from 'react'
import { ScrollView, View, type ViewProps } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Modal } from '@/src/elements/modal/Modal'
import { PlaceholderLine } from '@/src/elements/placeholder/PlaceholderLine'
import { BarStepper } from '@/src/elements/stepper/bar'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import type { useIntentionsPrayFlow } from '../../hooks/useIntentionsPrayFlow'
import { IntentionsPrayFlowPrayStep } from './IntentionsPrayFlowPrayStep'
import { IntentionsPrayFlowThankStep } from './IntentionsPrayFlowThankStep'

const getStepStyle = (totalSteps: number): ViewProps['style'] => ({ width: `${50 / totalSteps}%` })

export type TIntentionsPrayFlowBaseProps = ReturnType<typeof useIntentionsPrayFlow> & {
  onConfirmClose: VoidFunction
}

export const IntentionsPrayFlowBase = ({
  intentions,
  isLoading,
  next,
  pray,
  screenIndex,
  step,
  toIntentionPrayerIds,
  onConfirmClose,
}: TIntentionsPrayFlowBaseProps): ReactNode => {
  const [showCloseModal, setShowCloseModal] = useState(false)
  const { t } = useTranslate()

  const totalSteps = intentions?.length || 0

  return (
    <ScrollView className="flex h-full web:h-screen w-screen" testID={TEST_ID.INTENTIONS.SCREEN.PRAY_SCREEN}>
      <View className="native:min-h-full web:min-h-screen w-full pb-8">
        <View className="flex-row items-center justify-between gap-2 p-4">
          <View className="h-14 w-14" />
          <Typography bold center colors="primary" size="lg" className="grow">
            {t('watchman.daily.intentions')}
          </Typography>
          <Button
            isIconOnly
            startIcon={ICON.CLOSE}
            variant="light"
            contrast="low"
            colors="primary"
            testID={TEST_ID.PRAY.SCREEN.CLOSE}
            onPress={() => setShowCloseModal(true)}
          />
        </View>

        <View className="mx-auto w-full max-w-lg px-4">
          {isLoading ? (
            <PlaceholderLine className="animate-pulse h-1.5" />
          ) : (
            <BarStepper currentStep={step} totalSteps={totalSteps} />
          )}
        </View>

        <View className="grow overflow-hidden max-w-lg mx-auto w-full native:max-h-full">
          {isLoading && (
            <View className="grow px-4 py-6">
              <View className="grow gap-3 justify-center">
                <PlaceholderLine size="xl" className="max-w-48 animate-pulse" />
                <PlaceholderLine className="animate-pulse" />
                <PlaceholderLine className="animate-pulse" />
              </View>
              <Button isDisabled fullWidth variant="solid" colors="primary">
                {t('pray.pray')}
              </Button>
            </View>
          )}

          {!!intentions?.length && (
            <View
              className="grow flex-row items-stretch pt-6 transition-transform duration-300 native:max-h-full"
              style={{
                width: `${totalSteps * 200}%`,
                transform: [{ translateX: `${(-1 * (screenIndex * 50)) / totalSteps}%` }],
              }}
            >
              {intentions.map((dailyIntention) => (
                <Fragment key={dailyIntention.intention.id}>
                  <IntentionsPrayFlowPrayStep
                    dailyIntention={dailyIntention}
                    onPrayed={pray}
                    onAmen={next}
                    isLoading={isLoading}
                    paused={showCloseModal}
                    style={getStepStyle(totalSteps)}
                  />
                  <IntentionsPrayFlowThankStep
                    dailyIntention={dailyIntention}
                    onContinue={next}
                    prayerId={toIntentionPrayerIds[dailyIntention.intention.id]}
                    style={getStepStyle(totalSteps)}
                  />
                </Fragment>
              ))}
            </View>
          )}
        </View>
      </View>
      <Modal
        showModal={showCloseModal}
        onCancel={() => setShowCloseModal(false)}
        onConfirm={() => {
          setShowCloseModal(false)
          onConfirmClose()
        }}
        title={t('watchman.daily.close')}
        description={t('watchman.daily.remaining', { count: totalSteps + 1 - step })}
        showCancelButton
        confirmText={t('word.stop')}
        cancelText={t('watchman.daily.continue')}
      />
    </ScrollView>
  )
}
